import { colors } from "@buildwithflux/models";
import { range } from "@buildwithflux/shared";
import { clone } from "lodash";
import { DOMImplementation, XMLSerializer } from "xmldom";
import { SymbolHelpers } from "./SymbolHelpers";
export var StrokeLineStyle;
(function (StrokeLineStyle) {
    StrokeLineStyle[StrokeLineStyle["Solid"] = 0] = "Solid";
    StrokeLineStyle[StrokeLineStyle["Dashed"] = 1] = "Dashed";
    StrokeLineStyle[StrokeLineStyle["Dotted"] = 2] = "Dotted";
    StrokeLineStyle[StrokeLineStyle["DashDot"] = 3] = "DashDot";
})(StrokeLineStyle || (StrokeLineStyle = {}));
export class BaseSvgConverter {
    symbolCenterToSymbolOrigin = {
        dX: 0,
        dY: 0,
    };
    document = new DOMImplementation().createDocument("http://www.w3.org/1999/xhtml", "html");
    svgContainer = this.document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svgBoundary = {
        start: undefined,
        end: undefined,
    };
    svgPathElements = [];
    defaultLineThickness = 1;
    getSvgString() {
        const xmlSerializer = new XMLSerializer();
        const svgString = xmlSerializer.serializeToString(this.svgContainer);
        return `<?xml version="1.0" encoding="UTF-8"?>${svgString}`;
    }
    getSvgContainer() {
        return this.svgContainer;
    }
    getSvgBoundary() {
        return clone(this.svgBoundary);
    }
    /**
     * Removes all fills from the SVG.
     */
    removeFills() {
        for (const path of this.svgPathElements) {
            path.setAttribute("fill", "none");
        }
    }
    // eslint-disable-next-line @typescript-eslint/member-ordering
    TEMP_drawArc(x1, y1, x2, y2, x3, y3, thickness) {
        y1 = -y1;
        y2 = -y2;
        y3 = -y3;
        this.updateBoundaryFromPoint(x1, y1);
        this.updateBoundaryFromPoint(x2, y2);
        this.updateBoundaryFromPoint(x3, y3);
        // Calculate midpoints
        const mid1 = { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
        const mid2 = { x: (x2 + x3) / 2, y: (y2 + y3) / 2 };
        // Slopes of P1P2 and P2P3
        const slope1 = (y2 - y1) / (x2 - x1);
        const slope2 = (y3 - y2) / (x3 - x2);
        // Perpendicular slopes
        const perpSlope1 = -1 / slope1;
        const perpSlope2 = -1 / slope2;
        // Intersection of the perpendicular bisectors
        const cx = (perpSlope1 * mid1.x - perpSlope2 * mid2.x + mid2.y - mid1.y) / (perpSlope1 - perpSlope2);
        const cy = perpSlope1 * (cx - mid1.x) + mid1.y;
        // Radius
        const r = Math.sqrt((cx - x1) ** 2 + (cy - y1) ** 2);
        // Large-arc-flag and sweep-flag
        const largeArcFlag = 0; // Assuming the smaller arc
        const sweepFlag = 0; //1; // Assuming clockwise direction
        // Create SVG path string
        const direction = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} ${sweepFlag} ${x3} ${y3}`;
        const path = this.constructPath(direction, thickness);
        this.svgContainer.appendChild(path);
    }
    configureSvgContainer() {
        if (this.svgBoundary.start !== undefined && this.svgBoundary.end !== undefined) {
            // Without a minimum dimension of 1, we can end up with broken svg's as a result of 'zeroed'
            // svg dimensions
            const width = Math.max(1, this.svgBoundary.end.x - this.svgBoundary.start.x);
            const height = Math.max(1, this.svgBoundary.start.y - this.svgBoundary.end.y);
            const left = this.svgBoundary.start.x;
            const top = this.svgBoundary.end.y;
            this.symbolCenterToSymbolOrigin = {
                dX: -(left + width / 2),
                dY: top + height / 2,
            };
            this.svgContainer.setAttribute("version", "1.1");
            this.svgContainer.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
            this.svgContainer.setAttribute("width", `${width}`);
            this.svgContainer.setAttribute("height", `${height}`);
            this.svgContainer.setAttribute("viewBox", `${left} ${top} ${width} ${height}`);
        }
        else {
            // TODO: Figure out logging handler
        }
    }
    constructPath(direction, thickness, lineStyle = StrokeLineStyle.Solid) {
        const path = this.document.createElementNS("http://www.w3.org/2000/svg", "path");
        // TODO: add support for fill/no-fill based on KiCad spec
        // TODO: resolve downstream issues handling no-fill SVG shapes. This commented-out line will cause bugs
        // when importing some parts:
        path.setAttribute("stroke", colors.elements.foreground);
        path.setAttribute("stroke-width", String(thickness));
        path.setAttribute("stroke-linecap", "round");
        path.setAttribute("stroke-dasharray", this.getStrokeDashArray(lineStyle));
        // Needed to ensure close paths don't produce an undesirable fill. This was found to
        // be a problem for MOSFET symbol in EDIF. Closes <path> created a filled circle - opaque
        // fill hid symbol geometry behind the circle. Note the TODO mentioned above, but also note
        // we may need control over fill that's non-Kicad specific
        path.setAttribute("fill-opacity", "0");
        path.setAttribute("d", direction);
        this.svgPathElements.push(path);
        return path;
    }
    constructText(position, text, thickness, italic, size) {
        const path = this.document.createElementNS("http://www.w3.org/2000/svg", "text");
        path.setAttribute("stroke", colors.elements.foreground);
        path.setAttribute("x", position.x.toString());
        path.setAttribute("y", position.y.toString());
        path.setAttribute("font-style", italic ? "italic" : "normal");
        path.setAttribute("font-size", size.toString());
        // TODO: provide this font
        // path.setAttribute("font-family", "ocr-a");
        path.setAttribute("stroke-width", String(thickness));
        path.innerHTML = text;
        return path;
    }
    polarToCartesian(radius, angleInDegrees) {
        const halfCircleDegrees = 180.0;
        const angleInRadians = (angleInDegrees * Math.PI) / halfCircleDegrees;
        return {
            x: Math.round(radius * Math.cos(angleInRadians)),
            y: Math.round(radius * Math.sin(angleInRadians)),
        };
    }
    drawLine(startX, startY, endX, endY, thickness = this.defaultLineThickness) {
        this.updateBoundaryFromPoint(startX, startY);
        this.updateBoundaryFromPoint(endX, endY);
        return this.constructPath(`M${startX},${startY} L${endX},${endY}`, thickness);
    }
    // Pushes boundary outward so point is included within boundary:
    updateBoundaryFromPoint(x, y /*TODO: add this as a optional threshold? thickness:number - to address 'zero dimension' cases */) {
        if (this.svgBoundary.start !== undefined) {
            this.svgBoundary.start.x = x < this.svgBoundary.start.x ? x : this.svgBoundary.start.x;
            this.svgBoundary.start.y = y > this.svgBoundary.start.y ? y : this.svgBoundary.start.y;
        }
        else {
            this.svgBoundary.start = { x, y };
        }
        if (this.svgBoundary.end !== undefined) {
            this.svgBoundary.end.x = x > this.svgBoundary.end.x ? x : this.svgBoundary.end.x;
            this.svgBoundary.end.y = y < this.svgBoundary.end.y ? y : this.svgBoundary.end.y;
        }
        else {
            this.svgBoundary.end = { x, y };
        }
    }
    createRectangle(startX, startY, endX, endY, thickness) {
        const topPath = this.drawLine(startX, startY, endX, startY, thickness);
        const rightPath = this.drawLine(endX, startY, endX, endY, thickness);
        const bottomPath = this.drawLine(endX, endY, startX, endY, thickness);
        const leftPath = this.drawLine(startX, endY, startX, startY, thickness);
        this.svgContainer.appendChild(topPath);
        this.svgContainer.appendChild(rightPath);
        this.svgContainer.appendChild(bottomPath);
        this.svgContainer.appendChild(leftPath);
    }
    createTrapezoid(startX, startY, endX, endY, thickness, delta) {
        //TODO: this might need work
        const topPath = this.drawLine(startX + delta.x, startY + delta.y, endX + delta.x, startY + delta.y, thickness);
        const rightPath = this.drawLine(endX + delta.x, startY + delta.y, endX, endY, thickness);
        const bottomPath = this.drawLine(endX, endY, startX, endY, thickness);
        const leftPath = this.drawLine(startX + delta.x, endY, startX, startY + delta.y, thickness);
        this.svgContainer.appendChild(topPath);
        this.svgContainer.appendChild(rightPath);
        this.svgContainer.appendChild(bottomPath);
        this.svgContainer.appendChild(leftPath);
    }
    createPolyline(thickness, points, lineStyle = StrokeLineStyle.Solid) {
        const path = this.drawPolyline(thickness, points, lineStyle);
        this.svgContainer.appendChild(path);
    }
    createText(strokeWidth, position, text, italic, size) {
        const path = this.drawText(strokeWidth, position, text, italic, size);
        this.svgContainer.appendChild(path);
    }
    createCircle(posX, posY, radius, thickness) {
        const position = `M${posX - radius}, ${posY}`;
        const semiCircle1 = `a${radius},${radius} 0 1,1 ${radius * 2},0`;
        const semiCircle2 = `a${radius},${radius} 0 1,1 ${-(radius * 2)},0`;
        const direction = `${position} ${semiCircle1} ${semiCircle2}`;
        this.updateBoundaryFromPoint(posX + radius, posY);
        this.updateBoundaryFromPoint(posX - radius, posY);
        this.updateBoundaryFromPoint(posX, posY + radius);
        this.updateBoundaryFromPoint(posX, posY - radius);
        const path = this.constructPath(direction, thickness);
        this.svgContainer.appendChild(path);
    }
    createArc(posX, posY, radius, startAngle, endAngle, _startX, _startY, _endX, _endY, thickness) {
        const startPointRelativeToArcCenter = this.polarToCartesian(radius, startAngle);
        const startPoint = {
            x: posX + startPointRelativeToArcCenter.x,
            y: posY + SymbolHelpers.invertAxis(startPointRelativeToArcCenter.y),
        };
        const endPointRelativeToArcCenter = this.polarToCartesian(radius, endAngle);
        const endPoint = {
            x: posX + endPointRelativeToArcCenter.x,
            y: posY + SymbolHelpers.invertAxis(endPointRelativeToArcCenter.y),
        };
        const halfCircleDegrees = 180;
        const largeArcFlag = endAngle - startAngle <= halfCircleDegrees ? "0" : "1";
        const direction = [
            "M",
            startPoint.x,
            startPoint.y,
            "A",
            radius,
            radius,
            0,
            largeArcFlag,
            0,
            endPoint.x,
            endPoint.y,
        ].join(" ");
        const countOfAngles = Math.floor(Math.abs(endAngle - startAngle));
        const arcAngles = range(countOfAngles, startAngle);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        arcAngles.forEach((angle) => {
            const arcPointRelativeToArcCenter = this.polarToCartesian(radius, angle);
            const arcPoint = {
                x: posX + arcPointRelativeToArcCenter.x,
                y: posY + SymbolHelpers.invertAxis(arcPointRelativeToArcCenter.y),
            };
            this.updateBoundaryFromPoint(arcPoint.x, arcPoint.y);
        });
        const path = this.constructPath(direction, thickness);
        this.svgContainer.appendChild(path);
    }
    createPolygon(points) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        points.forEach((point) => this.updateBoundaryFromPoint(point.x, point.y));
        const polygon = this.document.createElementNS("http://www.w3.org/2000/svg", "polygon");
        // TODO: add support for fill/no-fill based on KiCad spec
        // TODO: resolve downstream issues handling no-fill SVG shapes. This commented-out line will cause bugs
        // when importing some parts:
        polygon.setAttribute("fill", colors.elements.foreground);
        polygon.setAttribute("points", points.map((point) => `${point.x},${point.y}`).join(" "));
        this.svgPathElements.push(polygon);
        return polygon;
    }
    drawPolyline(thickness, points, lineStyle = StrokeLineStyle.Solid) {
        let direction = "";
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        points.forEach((point, index) => {
            this.updateBoundaryFromPoint(point.x, point.y);
            if (index === 0) {
                direction = `M${point.x},${point.y}`;
            }
            else {
                direction = `${direction} L${point.x},${point.y}`;
            }
        });
        return this.constructPath(direction, thickness, lineStyle);
    }
    drawText(strokeWidth, position, text, italic, size) {
        return this.constructText(position, text, strokeWidth, italic, size.y);
    }
    // Create the stroke-dasharray string based on the stroke style
    // Trial-and-error was used to determine the values for the dash, dot, and dash-dot styles
    getStrokeDashArray(strokeStyle) {
        switch (strokeStyle) {
            case StrokeLineStyle.Dashed:
                return "4,4"; // dash, gap, ...repeat
            case StrokeLineStyle.Dotted:
                return "1,2"; // dot, gap, ...repeat
            case StrokeLineStyle.DashDot:
                return "4,4,1,4"; // dash, gap, dot, gap, ...repeat
            case StrokeLineStyle.Solid:
            default:
                return "none";
        }
    }
}
//# sourceMappingURL=BaseSvgConverter.js.map